import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DollarSign,
  Plus,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  History,
  X,
  Save,
  PowerOff,
  Info,
  Search,
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface PricingRow {
  id: number;
  model: string;
  provider: string;
  input_price_per_million: number | null;
  output_price_per_million: number | null;
  cache_write_price_per_million: number | null;
  cache_read_price_per_million: number | null;
  effective_date: string;
  end_date: string | null;
  source: string | null;
  notes: string | null;
  created_at: string;
  is_active: boolean;
}

interface PricingGroup {
  model: string;
  provider: string;
  active: PricingRow | null;
  history: PricingRow[];
}

interface StalenessEntry {
  provider: string;
  model: string;
  events: number;
  first_seen: string;
  last_seen: string;
}

const PROVIDER_OPTIONS = [
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'mistral', label: 'Mistral' },
  { value: 'google', label: 'Google (Gemini)' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'other', label: 'Other' },
];

const ModelPricing: React.FC = () => {
  const [groups, setGroups] = useState<PricingGroup[]>([]);
  const [staleness, setStaleness] = useState<StalenessEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showNewRowModal, setShowNewRowModal] = useState(false);
  const [prefillForNewRow, setPrefillForNewRow] = useState<{ model: string; provider: string } | null>(null);
  const [showNotesModal, setShowNotesModal] = useState<PricingRow | null>(null);

  const fetchPricing = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/model-pricing');
      if (response.data && response.data.success) {
        setGroups(response.data.pricing || []);
        setStaleness(response.data.staleness || []);
      } else {
        toast.error('Failed to load pricing data');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (providerFilter && g.provider !== providerFilter) return false;
      if (!term) return true;
      return g.model.toLowerCase().includes(term) || g.provider.toLowerCase().includes(term);
    });
  }, [groups, search, providerFilter]);

  const handleSeedFromStaleness = (entry: StalenessEntry) => {
    setPrefillForNewRow({ model: entry.model, provider: entry.provider });
    setShowNewRowModal(true);
  };

  const handleDeactivate = async (row: PricingRow) => {
    const confirm = window.confirm(
      `Deactivate ${row.provider}/${row.model}? This sets end_date to today. Events captured after today for this model will have no matching price until you add a new pricing row.`
    );
    if (!confirm) return;
    try {
      const response = await api.post(`/api/admin/model-pricing/${row.id}/deactivate`, {});
      if (response.data && response.data.success) {
        toast.success('Pricing row deactivated');
        fetchPricing();
      } else {
        toast.error(response.data?.error || 'Deactivation failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Deactivation failed');
    }
  };

  const handleSaveNotes = async (id: number, source: string | null, notes: string | null) => {
    try {
      const response = await api.patch(`/api/admin/model-pricing/${id}`, { source, notes });
      if (response.data && response.data.success) {
        toast.success('Notes updated');
        setShowNotesModal(null);
        fetchPricing();
      } else {
        toast.error(response.data?.error || 'Update failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Update failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-900">Model Pricing</h2>
            </div>
            <p className="text-gray-600 mt-1 max-w-3xl">
              Manage the pricing used to compute LLM cost in the Usage &amp; Cost Dashboard. Every pricing
              row is valid for a specific date range; historical rows are immutable so past cost reports
              stay accurate forever. To change prices, create a <strong>new row with a new effective date</strong> —
              the previous active row is end-dated automatically.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchPricing}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => {
                setPrefillForNewRow(null);
                setShowNewRowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add / Update Pricing
            </button>
          </div>
        </div>
      </div>

      {/* Staleness warnings — models in usage_events without pricing */}
      {staleness.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900">
                {staleness.length} model{staleness.length === 1 ? '' : 's'} appearing in usage logs without a matching pricing row
              </div>
              <p className="text-sm text-amber-800 mt-1">
                Cost will report as <code>NULL</code> for these events until pricing is registered. Click
                &quot;Add pricing&quot; next to a row to open the form pre-filled.
              </p>
              <div className="mt-3 space-y-2">
                {staleness.map((entry) => (
                  <div
                    key={`${entry.provider}::${entry.model}`}
                    className="flex items-center justify-between bg-white border border-amber-200 rounded-lg px-3 py-2"
                  >
                    <div className="text-sm">
                      <span className="font-mono font-semibold">{entry.provider}/{entry.model}</span>
                      <span className="ml-3 text-gray-600">
                        {entry.events.toLocaleString()} event{entry.events === 1 ? '' : 's'}
                      </span>
                      <span className="ml-3 text-gray-500">
                        since {new Date(entry.first_seen).toISOString().slice(0, 10)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleSeedFromStaleness(entry)}
                      className="text-sm text-blue-700 hover:text-blue-900 font-medium"
                    >
                      Add pricing →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search model or provider..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All providers</option>
            {PROVIDER_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Pricing table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Provider / Model</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Input $/M</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Output $/M</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Cache Write $/M</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Cache Read $/M</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Effective Since</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">History</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading pricing data...
                </td>
              </tr>
            )}
            {!loading && filteredGroups.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                  No pricing rows match your filters.
                </td>
              </tr>
            )}
            {!loading && filteredGroups.map((group) => {
              const key = `${group.provider}::${group.model}`;
              const active = group.active;
              const isExpanded = expanded[key];
              return (
                <React.Fragment key={key}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-mono text-gray-900">{group.model}</div>
                      <div className="text-xs text-gray-500 uppercase">{group.provider}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-900">
                      {active ? `$${(active.input_price_per_million ?? 0).toFixed(2)}` : <span className="text-red-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-900">
                      {active ? `$${(active.output_price_per_million ?? 0).toFixed(2)}` : <span className="text-red-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">
                      {active && active.cache_write_price_per_million !== null
                        ? `$${(active.cache_write_price_per_million ?? 0).toFixed(4)}`
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">
                      {active && active.cache_read_price_per_million !== null
                        ? `$${(active.cache_read_price_per_million ?? 0).toFixed(4)}`
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {active ? (
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>{active.effective_date}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-red-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span>No active pricing</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {group.history.length > 0 ? (
                        <button
                          onClick={() => setExpanded({ ...expanded, [key]: !isExpanded })}
                          className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900"
                        >
                          <History className="w-3.5 h-3.5" />
                          {group.history.length} row{group.history.length === 1 ? '' : 's'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => {
                            setPrefillForNewRow({ model: group.model, provider: group.provider });
                            setShowNewRowModal(true);
                          }}
                          className="px-2 py-1 text-xs text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
                          title="Create a new pricing row that supersedes the active one"
                        >
                          Update price
                        </button>
                        {active && (
                          <>
                            <button
                              onClick={() => setShowNotesModal(active)}
                              className="px-2 py-1 text-xs text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                              title="Edit source and notes (cosmetic only)"
                            >
                              Edit notes
                            </button>
                            <button
                              onClick={() => handleDeactivate(active)}
                              className="px-2 py-1 text-xs text-red-700 bg-red-50 rounded hover:bg-red-100"
                              title="Retire this model — sets end_date to today"
                            >
                              <PowerOff className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && group.history.length > 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-0 bg-gray-50">
                        <div className="py-3 px-8 border-l-4 border-gray-300">
                          <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">
                            Historical pricing (immutable)
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-600">
                                <th className="text-left py-1">Effective</th>
                                <th className="text-left py-1">Ended</th>
                                <th className="text-right py-1">Input $/M</th>
                                <th className="text-right py-1">Output $/M</th>
                                <th className="text-right py-1">Cache W</th>
                                <th className="text-right py-1">Cache R</th>
                                <th className="text-left py-1">Source / Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.history.map((row) => (
                                <tr key={row.id} className="text-gray-700 border-t border-gray-200">
                                  <td className="py-1.5 font-mono">{row.effective_date}</td>
                                  <td className="py-1.5 font-mono">{row.end_date}</td>
                                  <td className="py-1.5 text-right font-mono">
                                    ${(row.input_price_per_million ?? 0).toFixed(2)}
                                  </td>
                                  <td className="py-1.5 text-right font-mono">
                                    ${(row.output_price_per_million ?? 0).toFixed(2)}
                                  </td>
                                  <td className="py-1.5 text-right font-mono">
                                    {row.cache_write_price_per_million !== null
                                      ? `$${row.cache_write_price_per_million.toFixed(4)}`
                                      : '—'}
                                  </td>
                                  <td className="py-1.5 text-right font-mono">
                                    {row.cache_read_price_per_million !== null
                                      ? `$${row.cache_read_price_per_million.toFixed(4)}`
                                      : '—'}
                                  </td>
                                  <td className="py-1.5">
                                    <button
                                      onClick={() => setShowNotesModal(row)}
                                      className="text-blue-700 hover:text-blue-900"
                                    >
                                      {row.source || row.notes ? 'View / edit' : 'Add notes'}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* How price changes work info strip */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900 flex gap-3">
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">How price changes work</div>
          <p className="mt-1">
            Historical cost reports must stay accurate forever, so pricing is append-only. When you click
            &quot;Update price&quot;, the current active row is end-dated to the day before your new
            effective date, and your new row becomes the active one. Events captured before the change
            keep using the old price; events captured after use the new price. All mutations are
            audit-logged.
          </p>
        </div>
      </div>

      {showNewRowModal && (
        <NewPricingRowModal
          prefill={prefillForNewRow}
          onClose={() => {
            setShowNewRowModal(false);
            setPrefillForNewRow(null);
          }}
          onSaved={() => {
            setShowNewRowModal(false);
            setPrefillForNewRow(null);
            fetchPricing();
          }}
        />
      )}

      {showNotesModal && (
        <NotesModal
          row={showNotesModal}
          onClose={() => setShowNotesModal(null)}
          onSave={(source, notes) => handleSaveNotes(showNotesModal.id, source, notes)}
        />
      )}
    </div>
  );
};

// ─── New pricing row modal ──────────────────────────────────────────────────

interface NewPricingRowModalProps {
  prefill: { model: string; provider: string } | null;
  onClose: () => void;
  onSaved: () => void;
}

const NewPricingRowModal: React.FC<NewPricingRowModalProps> = ({ prefill, onClose, onSaved }) => {
  const [model, setModel] = useState(prefill?.model || '');
  const [provider, setProvider] = useState(prefill?.provider || 'anthropic');
  const [inputPrice, setInputPrice] = useState('');
  const [outputPrice, setOutputPrice] = useState('');
  const [cacheWrite, setCacheWrite] = useState('');
  const [cacheRead, setCacheRead] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [source, setSource] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!model.trim() || !provider) {
      toast.error('Model and provider are required');
      return;
    }
    const input = Number(inputPrice);
    const output = Number(outputPrice);
    if (!Number.isFinite(input) || input < 0 || !Number.isFinite(output) || output < 0) {
      toast.error('Input and output prices must be non-negative numbers');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/api/admin/model-pricing', {
        model: model.trim(),
        provider,
        input_price_per_million: input,
        output_price_per_million: output,
        cache_write_price_per_million: cacheWrite === '' ? null : Number(cacheWrite),
        cache_read_price_per_million: cacheRead === '' ? null : Number(cacheRead),
        effective_date: effectiveDate,
        source: source.trim() || null,
        notes: notes.trim() || null,
      });
      if (response.data && response.data.success) {
        toast.success(
          response.data.ended_previous
            ? 'New pricing saved; previous row end-dated automatically'
            : 'Pricing saved'
        );
        onSaved();
      } else {
        toast.error(response.data?.error || 'Save failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {prefill ? `Update pricing for ${prefill.provider}/${prefill.model}` : 'Add new pricing row'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model ID</label>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={!!prefill}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono disabled:bg-gray-100"
                placeholder="claude-opus-4-7"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                disabled={!!prefill}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-100"
              >
                {PROVIDER_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Input price ($/M tokens)</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={inputPrice}
                onChange={(e) => setInputPrice(e.target.value)}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
                placeholder="3.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Output price ($/M tokens)</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={outputPrice}
                onChange={(e) => setOutputPrice(e.target.value)}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
                placeholder="15.00"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cache write price ($/M) <span className="text-gray-400 font-normal">optional</span>
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={cacheWrite}
                onChange={(e) => setCacheWrite(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
                placeholder="3.75"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cache read price ($/M) <span className="text-gray-400 font-normal">optional</span>
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={cacheRead}
                onChange={(e) => setCacheRead(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
                placeholder="0.30"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effective date</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              required
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">
              The new row is active from this date onward. The currently-active row (if any) will have its
              end_date set to the day before.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source <span className="text-gray-400 font-normal">optional — where you got this price</span>
            </label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              placeholder="https://docs.anthropic.com/en/docs/about-claude/pricing"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400 font-normal">optional</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              placeholder="Announcement reference, caveats, batched rate if applicable..."
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save pricing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Notes edit modal ───────────────────────────────────────────────────────

interface NotesModalProps {
  row: PricingRow;
  onClose: () => void;
  onSave: (source: string | null, notes: string | null) => void;
}

const NotesModal: React.FC<NotesModalProps> = ({ row, onClose, onSave }) => {
  const [source, setSource] = useState(row.source || '');
  const [notes, setNotes] = useState(row.notes || '');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Edit notes — {row.provider}/{row.model}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Only source and notes can be edited. Prices and dates on this row are
            immutable — {row.is_active ? 'create a new row to change pricing' : 'this row is historical'}.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(source.trim() || null, notes.trim() || null)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelPricing;
